import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Alert Rule Types
// ---------------------------------------------------------------------------

export interface AlertRuleProduct {
  readonly id: string;
  readonly name: string;
  readonly sku: string;
  readonly brand: string;
}

export interface AlertRuleCategory {
  readonly id: string;
  readonly name: string;
}

export interface StockAlertRule {
  readonly id: string;
  readonly scope: "product" | "category";
  readonly product_id: string | null;
  readonly category_id: string | null;
  readonly threshold: number;
  readonly notify_email: boolean;
  readonly notify_sms: boolean;
  readonly notify_whatsapp: boolean;
  readonly auto_reorder: boolean;
  readonly is_active: boolean;
  readonly created_at: string;
  readonly updated_at: string;
  readonly product: AlertRuleProduct | null;
  readonly category: AlertRuleCategory | null;
}

export interface StockAlertRuleListResponse {
  readonly rules: readonly StockAlertRule[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface CreateStockAlertRuleInput {
  readonly scope: "product" | "category";
  readonly product_id?: string;
  readonly category_id?: string;
  readonly threshold: number;
  readonly notify_email: boolean;
  readonly notify_sms: boolean;
  readonly notify_whatsapp: boolean;
  readonly auto_reorder: boolean;
  readonly is_active: boolean;
}

export interface UpdateStockAlertRuleInput {
  readonly scope?: "product" | "category";
  readonly product_id?: string | null;
  readonly category_id?: string | null;
  readonly threshold?: number;
  readonly notify_email?: boolean;
  readonly notify_sms?: boolean;
  readonly notify_whatsapp?: boolean;
  readonly auto_reorder?: boolean;
  readonly is_active?: boolean;
}

// ---------------------------------------------------------------------------
// Inventory Stock Types
// ---------------------------------------------------------------------------

export type StockStatus = "in_stock" | "low_stock" | "out_of_stock";

export type MovementType =
  | "sale"
  | "return"
  | "restock"
  | "adjustment"
  | "damage";

export interface InventoryProduct {
  readonly id: string;
  readonly name: string;
  readonly sku: string;
  readonly brand: string;
  readonly category: string;
  readonly current_stock: number;
  readonly alert_threshold: number;
  readonly status: StockStatus;
  readonly image_url: string | null;
  readonly updated_at: string;
}

export interface InventoryListResponse {
  readonly products: readonly InventoryProduct[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface InventoryListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly q?: string | null;
  readonly brand?: string | null;
  readonly category?: string | null;
  readonly status?: StockStatus | null;
}

export interface StockMovement {
  readonly id: string;
  readonly product_id: string;
  readonly product_name: string;
  readonly product_sku: string;
  readonly type: MovementType;
  readonly quantity: number;
  readonly reason: string;
  readonly operator: string;
  readonly created_at: string;
}

export interface MovementListResponse {
  readonly movements: readonly StockMovement[];
  readonly pagination: {
    readonly page: number;
    readonly limit: number;
    readonly total: number;
  };
}

export interface MovementListParams {
  readonly page?: number;
  readonly limit?: number;
  readonly product_id?: string | null;
  readonly type?: MovementType | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface CreateMovementInput {
  readonly product_id: string;
  readonly type: MovementType;
  readonly quantity: number;
  readonly reason: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const inventoryApi = {
  alerts: {
    /** GET paginated list of stock alert rules. */
    list: (params?: { page?: number; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.page) qs.set("page", String(params.page));
      if (params?.limit) qs.set("limit", String(params.limit));
      const query = qs.toString();
      return adminFetch<StockAlertRuleListResponse>(
        `/api/admin/inventory/alerts${query ? `?${query}` : ""}`,
      );
    },

    /** POST create a new stock alert rule. */
    create: (data: CreateStockAlertRuleInput) =>
      adminFetch<StockAlertRule>("/api/admin/inventory/alerts", {
        method: "POST",
        body: JSON.stringify(data),
      }),

    /** PATCH update an existing stock alert rule. */
    update: (id: string, data: UpdateStockAlertRuleInput) =>
      adminFetch<StockAlertRule>(
        `/api/admin/inventory/alerts/${encodeURIComponent(id)}`,
        {
          method: "PATCH",
          body: JSON.stringify(data),
        },
      ),

    /** DELETE a stock alert rule. */
    delete: (id: string) =>
      adminFetch<{ success: boolean }>(
        `/api/admin/inventory/alerts/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
  },

  stock: {
    /** GET paginated list of products with current stock levels. */
    list: (params: InventoryListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        q: params.q,
        brand: params.brand,
        category: params.category,
        status: params.status,
      });
      return adminFetch<InventoryListResponse>(`/api/admin/inventory${qs}`);
    },

    /** GET paginated movement history, optionally filtered by product. */
    movements: (params: MovementListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        product_id: params.product_id,
        type: params.type,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<MovementListResponse>(
        `/api/admin/inventory/movements${qs}`,
      );
    },

    /** POST record a new stock movement. */
    createMovement: (data: CreateMovementInput) =>
      adminFetch<StockMovement>("/api/admin/inventory/movements", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
} as const;
